import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import profilesIcon from 'assets/images/icons/profiles.svg';
import trashRedIcon from 'assets/images/icons/trash-red-2.svg';
import AlertModal from 'components/common-components/AlertModal';
import TextField from 'components/form-components/TextField';
import BadgesWithSelection from 'components/common-components/BadgesWithSelection';
import FormButton from 'components/form-components/FormButton';
import FormLoadingButton from 'components/form-components/FormLoadingButton';
import TabSelectionField from 'components/form-components/TabSelectionField';
import { CallAPI } from 'actions/General';
import { AddDashboardSchema, AddPageSchema, DashboardDetailPagesTableFields } from './utils';
import TableComponent from 'components/table-component/Table';
import ConfirmationModal from 'components/common-components/ConfirmationModal';
import { cloneDeep } from 'lodash';

const AddDashboardOrPage = ({ type, editId, selectedDashboardId, refreshList, onCancel }) => {
  const { t } = useTranslation();
  const [values, setValues] = useState({ dashboardTitle: '', allowedProfiles: [] });
  const [pageValues, setPageValues] = useState({
    pageTitle: '',
    allowedProfiles: [],
    pageDirection: 'left_to_right',
  });
  const [pageList, setPageList] = useState([]);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [, setFetchLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [step, setStep] = useState(type);
  const [adminProfileList, setAdminProfileList] = useState([]);
  const [closeConfirmModal, setCloseConfirmModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleDashboardChange = (field, value) => {
    setTouched({ ...touched, [field]: true });
    setErrors({ ...errors, [field]: undefined });
    setValues({ ...values, [field]: value });
  };

  const handlePageChange = (field, value) => {
    setTouched({ ...touched, [field]: true });
    setErrors({ ...errors, [field]: undefined });
    setPageValues({ ...pageValues, [field]: value });
  };

  const onClose = () => {
    if (type === 'dashboard' && step === 'page') setCloseConfirmModal(true);
    else onCancel();
  };

  const nextStep = async () => {
    try {
      await AddDashboardSchema.validate(values, { abortEarly: false });

      setStep('page');
    } catch (error) {
      const schemaErrors = {};
      error.inner?.forEach((err) => {
        schemaErrors[err.path] = err.message;
      });
      setErrors(schemaErrors);
    }
  };

  const saveDashboard = async () => {
    try {
      if (!editId) await AddPageSchema.validate(pageValues, { abortEarly: false });

      const { dashboardTitle, allowedProfiles } = values;
      const payload = editId
        ? {
            dashboardTitle,
            allowedProfiles,
            pages: pageList.map((page) => ({
              pageId: page._id,
              allowedProfiles: page.allowedProfiles.map((p) => p._id),
            })),
          }
        : { dashboardTitle, allowedProfiles, page: pageValues };

      const apiType = editId ? 'UPDATE_DASHBOARD' : 'ADD_DASHBOARD';
      const successMsg = editId
        ? 'alert_success_dashboard_updated'
        : 'alert_success_dashboard_added';

      const result = await CallAPI(apiType, payload, editId, setSaveLoading);
      if (result?.status) {
        toast.success(t(successMsg));
        refreshList();
        onCancel();
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      console.log('error', error);
      const schemaErrors = {};
      error.inner?.forEach((err) => {
        schemaErrors[err.path] = err.message;
      });
      setErrors(schemaErrors);
    }
  };

  const saveDashboardPage = async () => {
    try {
      await AddPageSchema.validate(pageValues, { abortEarly: false });

      const { pageTitle, allowedProfiles, pageDirection } = pageValues;
      const payload = editId
        ? { pageTitle, allowedProfiles, pageDirection }
        : { pageTitle, allowedProfiles, pageDirection, dashboardId: selectedDashboardId };

      const apiType = editId ? 'UPDATE_PAGE' : 'ADD_PAGE';
      const successMsg = editId
        ? 'alert_success_dashboard_page_updated'
        : 'alert_success_dashboard_page_added';

      const result = await CallAPI(apiType, payload, editId, setSaveLoading);
      if (result?.status) {
        toast.success(t(successMsg));
        refreshList();
        onCancel();
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      console.log('error', error);
      const schemaErrors = {};
      error.inner?.forEach((err) => {
        schemaErrors[err.path] = err.message;
      });
      setErrors(schemaErrors);
    }
  };

  const deleteDashboardOrPage = async () => {
    const apiType =
      type === 'dashboard' ? 'DELETE_DASHBOARD' : type === 'page' ? 'DELETE_PAGE' : '';
    const successMsg =
      type === 'dashboard'
        ? 'alert_success_dashboard_deleted'
        : 'alert_success_dashboard_page_deleted';

    if (editId && apiType) {
      const result = await CallAPI(apiType, null, editId);
      if (result?.status) {
        toast.success(t(successMsg));
        refreshList();
        onCancel();
      } else {
        toast.error(result.error);
      }
    }
    setShowDeleteConfirm(false);
  };

  const getProfilesListForSelection = async () => {
    const result = await CallAPI(`LIST_ALL_ADMIN_PROFILES`, null, null, setFetchLoading);
    if (result.status) {
      setAdminProfileList(result.data);
    }
  };

  const getDashboardOrPageDetails = async () => {
    const API_URL = type === 'dashboard' ? 'GET_DASHBOARD_DETAIL' : 'GET_DASHBOARD_PAGE_DETAIL';
    const result = await CallAPI(API_URL, null, editId, setFetchLoading);
    if (result.status) {
      if (type === 'dashboard') {
        setValues({
          ...values,
          dashboardTitle: result.data.dashboardTitle ?? '',
          allowedProfiles: result.data.allowedProfiles.map((p) => p._id),
        });
        setPageList(result.data.pages);
      } else
        setPageValues({
          ...pageValues,
          pageTitle: result.data.pageTitle ?? '',
          allowedProfiles: result.data.allowedProfiles.map((p) => p._id),
          pageDirection: result.data.pageDirection ?? 'left_to_right',
        });
    }
  };

  const handlePageListChange = (field, values, pageId) => {
    const tempPageList = cloneDeep(pageList);
    const tempPage = tempPageList.find((p) => p._id === pageId);
    if (tempPage) {
      tempPage[field] = adminProfileList.filter((p) => values.includes(p._id));
      setPageList(tempPageList);
    }
  };

  useEffect(() => {
    if (editId) {
      getDashboardOrPageDetails();
    }
  }, [editId]);

  useEffect(() => {
    getProfilesListForSelection();
  }, []);

  return (
    <Fragment>
      <AlertModal show={true} actionButtons={[]} noPadding size='lg' enforceFocus={false}>
        <div className='dashboard-page-modal'>
          <div className='p-3 theme-background-white-5'>
            {step === 'dashboard' ? (
              <label className='theme-text-black-1 theme-size-1_2 mb-2'>
                {t(editId ? 'text_dashboard_settings' : 'text_add_new_dashboard')}
              </label>
            ) : (
              <label className='theme-text-black-1 theme-size-1_2 mb-2'>
                {t(editId ? 'text_page_settings' : 'text_add_new_page')}
              </label>
            )}

            {type === 'dashboard' ? (
              <>
                {step === 'dashboard' ? (
                  <AddDashboard
                    values={values}
                    pageList={pageList}
                    handlePageListChange={handlePageListChange}
                    errors={errors}
                    touched={touched}
                    handleChange={handleDashboardChange}
                    adminProfileList={adminProfileList}
                  />
                ) : (
                  <AddPage
                    values={pageValues}
                    errors={errors}
                    touched={touched}
                    handleChange={handlePageChange}
                    adminProfileList={adminProfileList}
                  />
                )}
              </>
            ) : type === 'page' ? (
              <>
                <AddPage
                  values={pageValues}
                  errors={errors}
                  touched={touched}
                  handleChange={handlePageChange}
                  adminProfileList={adminProfileList}
                />
              </>
            ) : (
              <></>
            )}
          </div>
          <div className='d-flex justify-content-between p-3'>
            <div>
              <FormButton
                text={'button_delete'}
                variant='red-3'
                onClick={() => setShowDeleteConfirm(true)}
                icon={trashRedIcon}
                disabled={!editId}
                minWidth
              />
            </div>
            <div className='d-flex gap-3'>
              <FormButton text={'button_close'} variant='white-1' onClick={onClose} minWidth />
              {type === 'dashboard' ? (
                <>
                  {step === 'dashboard' && !editId ? (
                    <FormButton
                      text={'button_next'}
                      variant='green-1'
                      disabled={values.dashboardTitle === ''}
                      onClick={nextStep}
                      minWidth
                    />
                  ) : (
                    <FormLoadingButton
                      text={'button_save'}
                      variant='green-1'
                      loading={saveLoading}
                      onClick={saveDashboard}
                      minWidth
                    />
                  )}
                </>
              ) : type === 'page' ? (
                <FormLoadingButton
                  text={'button_save'}
                  variant='green-1'
                  loading={saveLoading}
                  onClick={saveDashboardPage}
                  disabled={!(selectedDashboardId || editId)}
                  minWidth
                />
              ) : (
                <></>
              )}
            </div>
          </div>
        </div>
      </AlertModal>
      <ConfirmationModal
        show={closeConfirmModal}
        title={t('alert_dashboard_leave_confirmation_title')}
        message={t('alert_dashboard_leave_confirmation_message')}
        actionList={[
          { color: 'red-1', text: t('button_leave'), onClick: onCancel },
          {
            color: 'green-1',
            text: t('button_continue'),
            onClick: () => setCloseConfirmModal(false),
          },
        ]}
      />
      {!!showDeleteConfirm && (
        <ConfirmationModal
          show={!!showDeleteConfirm}
          title={t(
            type === 'dashboard'
              ? 'alert_dashboard_deletion_title'
              : 'alert_dashboard_page_deletion_title',
          )}
          message={t(
            type === 'dashboard'
              ? 'alert_dashboard_deletion_message'
              : 'alert_dashboard_page_deletion_message',
          )}
          actionList={[
            {
              color: 'black-1',
              text: t('button_cancel'),
              onClick: () => setShowDeleteConfirm(false),
            },
            { color: 'red-1', text: t('button_delete'), onClick: () => deleteDashboardOrPage() },
          ]}
        />
      )}
    </Fragment>
  );
};

AddDashboardOrPage.propTypes = {
  type: PropTypes.string.isRequired,
  editId: PropTypes.string,
  selectedDashboardId: PropTypes.string,
  refreshList: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default AddDashboardOrPage;

const AddDashboard = ({
  values,
  pageList,
  handlePageListChange,
  errors,
  touched,
  handleChange,
  adminProfileList,
}) => {
  const { t } = useTranslation();
  const adminProfileOptions = adminProfileList.map((p) => ({
    value: p._id,
    displayValue: p.profileName,
  }));
  return (
    <Fragment>
      <TextField
        value={values.dashboardTitle}
        name='dashboardTitle'
        classes='w-auto mb-3'
        handleChange={handleChange}
        placeholder='field_name'
        displayName='field_name'
        error={errors.dashboardTitle}
        touched={touched.dashboardTitle}
        shrinkFieldOnly
      />

      <BadgesWithSelection
        name='allowedProfiles'
        displayName='text_dashboard_available_to'
        hintText='text_select_profile_to_give_give_access_for_this_dashboard'
        icon={profilesIcon}
        optionList={adminProfileOptions}
        selectedList={values.allowedProfiles}
        handleChange={handleChange}
      />
      <label className='mt-3 mb-2'>{t('text_pages_access')}</label>
      <div className='theme-background-white-1 rounded-sm p-3'>
        <div className='border-grey-1-h-1 rounded-sm overflow-hidden'>
          <TableComponent
            header={DashboardDetailPagesTableFields}
            data={pageList}
            props={{ getAndUpdatedAdminProfiles: { adminProfileOptions, handlePageListChange } }}
            totalRecords={pageList.length}
            filterEnabled={false}
            loader={false}
            maintainTableHeight={false}
            isPaginationEnabled={false}
            isSelectionEnabled={false}
            isActionsEnabled={false}
            isFieldSelectionEnabled={false}
          />
        </div>
      </div>
    </Fragment>
  );
};

AddDashboard.propTypes = {
  values: PropTypes.object.isRequired,
  pageList: PropTypes.array.isRequired,
  handlePageListChange: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  touched: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
  adminProfileList: PropTypes.array.isRequired,
};

const AddPage = ({ values, errors, touched, handleChange, adminProfileList }) => {
  const { t } = useTranslation();
  const pageDirectionValues = [
    { value: 'left_to_right', displayValue: 'select_left_to_right' },
    { value: 'right_to_left', displayValue: 'select_right_to_left' },
  ];
  const adminProfileOptions = adminProfileList.map((p) => ({
    value: p._id,
    displayValue: p.profileName,
  }));

  return (
    <Fragment>
      <TextField
        value={values.pageTitle}
        name='pageTitle'
        classes='w-auto mb-3'
        handleChange={handleChange}
        placeholder='field_name'
        displayName='field_name'
        error={errors.pageTitle}
        touched={touched.pageTitle}
        shrinkFieldOnly
      />
      <BadgesWithSelection
        name='allowedProfiles'
        displayName='text_page_available_to'
        hintText='text_none'
        icon={profilesIcon}
        optionList={adminProfileOptions}
        selectedList={values.allowedProfiles}
        handleChange={handleChange}
      />
      <label htmlFor='pageDirection' className='mt-3'>
        {t('text_page_layout_direction')}
      </label>
      <TabSelectionField
        name='pageDirection'
        value={values.pageDirection}
        optionList={pageDirectionValues}
        handleChange={handleChange}
        classes='mt-1'
      />
    </Fragment>
  );
};

AddPage.propTypes = {
  values: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  touched: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
  adminProfileList: PropTypes.array.isRequired,
};
